import { useState } from "react";
import { toast } from "sonner";
import { synthesizeJamLayers } from "@/lib/instantBeat";
import { padCh } from "@/components/daw/dawHelpers";

/**
 * Jam Mode + Vocal Cleanup apply — extracted verbatim from ELPMStudioDAW.
 * Both load freshly rendered AudioBuffers onto channels and keep the DAW
 * transport React state in sync.
 */
export default function useChannelRenderFlows({
  engine,
  setPlaying,
  setTempo,
  setMaxDur,
  setLoadedCount,
  setSingCtaInfo,
}) {
  const [busyJam, setBusyJam] = useState(false);

  /** Jam Mode — render coordinated drums + bass + chord pad as 3 channels. */
  const handleJam = async () => {
    if (busyJam) return;
    setBusyJam(true);
    let loadToastId = null;
    try {
      if (engine.ctx.state === "suspended") await engine.ctx.resume();
      const t0 = performance.now();
      const layers = await synthesizeJamLayers(engine.ctx, {
        onLoadProgress: ({ stage, genre: g }) => {
          if (stage === "loading") {
            loadToastId = toast.loading(`Jamming on ${g}…`, {
              description: "Loading real drums + bass + piano samples (cached after first run).",
            });
          } else if (stage === "rendering" && loadToastId) {
            toast.dismiss(loadToastId);
            loadToastId = null;
          }
        },
      });
      if (loadToastId) { toast.dismiss(loadToastId); loadToastId = null; }

      const free = engine.freeChannelIndices(3);
      if (free.length < 3) {
        toast.error("Need 3 free channels for Jam Mode — clear some channels first.");
        return;
      }
      const [drumIdx, bassIdx, padIdx] = free;
      engine.setTempo(layers.tempo);
      engine.loadBufferToChannel(drumIdx, layers.drums, `Jam · Drums · ${layers.genre}`);
      engine.loadBufferToChannel(bassIdx, layers.bass, `Jam · Bass · ${layers.genre}`);
      engine.loadBufferToChannel(padIdx, layers.pad, `Jam · Chords · ${layers.genre}`);

      engine.stop();
      engine.seekTo(0);
      engine.setLoop(true, 0, layers.drums.duration);
      await engine.play();

      setPlaying(true);
      setTempo(layers.tempo);
      setMaxDur(engine.maxDuration);
      setLoadedCount(engine.channels.filter((c) => c.buffer).length);
      setSingCtaInfo({ genre: layers.genre, tempo: layers.tempo });

      const elapsedMs = Math.round(performance.now() - t0);
      toast.success(`Jam ready · ${layers.genre} @ ${layers.tempo} BPM`, {
        description: `3 channels loaded · CH ${padCh(drumIdx)} (drums) · CH ${padCh(bassIdx)} (bass) · CH ${padCh(padIdx)} (chords) · ${elapsedMs} ms`,
        duration: 6000,
      });
    } catch (e) {
      if (loadToastId) toast.dismiss(loadToastId);
      toast.error("Jam Mode failed", { description: e.message });
    } finally {
      setBusyJam(false);
    }
  };

  /** iter-224 — Apply cleaned audio (MP3 bytes from ElevenLabs Audio
   *  Isolation) to a channel, blended with the dry buffer at the
   *  user-chosen strength. The dry buffer is stashed so undo can revert. */
  const applyVocalCleanup = async (ch, cleanedAb, strength) => {
    const channel = engine.channels[ch];
    if (!channel?.buffer) throw new Error("Channel has no audio to clean");
    const cleanBuf = await engine.ctx.decodeAudioData(cleanedAb.slice(0));
    const dryBuf = channel.buffer;
    const wet = Math.max(0, Math.min(1, strength / 100));
    const dry = 1 - wet;
    const sr = engine.ctx.sampleRate;
    const outChannels = Math.max(dryBuf.numberOfChannels, cleanBuf.numberOfChannels);
    const outLen = Math.max(dryBuf.length, cleanBuf.length);
    const out = engine.ctx.createBuffer(outChannels, outLen, sr);
    for (let c = 0; c < outChannels; c++) {
      const od = out.getChannelData(c);
      const dd = dryBuf.getChannelData(Math.min(c, dryBuf.numberOfChannels - 1));
      const cd = cleanBuf.getChannelData(Math.min(c, cleanBuf.numberOfChannels - 1));
      const dLen = dd.length, cLen = cd.length;
      for (let i = 0; i < outLen; i++) {
        const dv = i < dLen ? dd[i] : 0;
        const cv = i < cLen ? cd[i] : 0;
        od[i] = dry * dv + wet * cv;
      }
    }
    engine._stashUndo(ch, dryBuf, channel.name || `Channel-${padCh(ch)}`);
    const baseName = (channel.name || `CH${padCh(ch)}`).replace(/\.[^.]+$/, "");
    engine.loadBufferToChannel(ch, out, `${baseName} · Cleaned ${strength}%`, channel.sourceUrl);
    setMaxDur(engine.maxDuration);

    // iter-225 — Persistent A/B compare toast. Each press swaps the channel's
    // buffer with the undo cache and re-renders the toast.
    const showCompareToast = (currentlyClean) => {
      toast.success(
        currentlyClean
          ? `CH ${padCh(ch)} · playing CLEANED`
          : `CH ${padCh(ch)} · playing DRY`,
        {
          description: currentlyClean
            ? `${strength}% clean / ${100 - strength}% dry · click A/B to hear the original`
            : "Original take · click A/B to hear the cleaned version",
          action: {
            label: "A/B compare",
            onClick: () => {
              if (engine.swapAB(ch)) {
                setMaxDur(engine.maxDuration);
                showCompareToast(!currentlyClean);
              } else {
                toast.message("Nothing to compare — undo cache cleared.");
              }
            },
          },
          duration: 20000,
        }
      );
    };
    showCompareToast(true);
  };

  return { busyJam, handleJam, applyVocalCleanup };
}
